import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { StarRounded, WorkspacePremiumRounded, PaidRounded, EventRounded, VerifiedRounded, CardGiftcardRounded } from '@mui/icons-material';
import { Crumbs, DetailHead, TabBar, KV, StatBox, SectionCard, Grid, useTab } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import NameCell from '../../components/NameCell';
import ProgressBar from '../../components/ProgressBar';
import TrialEditDialog from '../../components/TrialEditDialog';
import { useBusinessDetail } from '../../hooks/useBusinesses';
import { money, statusLabel, initials, avatarGradient } from '../../utils/format';
import { colors } from '../../theme/theme';

const teamRoleStatus = (role: string) =>
  /admin|administrator/i.test(role) ? 'info' : /supervisor/i.test(role) ? 'warning' : 'success';

const Stars: React.FC<{ n: number }> = ({ n }) => (
  <Box sx={{ display: 'inline-flex' }}>
    {Array.from({ length: 5 }, (_, i) => <StarRounded key={i} sx={{ fontSize: 16, color: i < n ? '#F59E0B' : '#E5E7EB' }} />)}
  </Box>
);

const BusinessDetailPage: React.FC = () => {
  const { id } = useParams();
  const { data, loading, error, reload } = useBusinessDetail(id);
  const [tab, setTab] = useTab('overview');
  const [editTrial, setEditTrial] = useState(false);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !data) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'Not found.'}</Alert>;
  }

  const b = data.business;
  const s = data.stats;
  const sub = data.subscription;
  const trial = data.trial;
  const planLabel = sub ? (sub.is_trial ? 'Trial' : statusLabel(String(sub.plan_type || 'Plan'))) : 'No plan';
  const renewal = sub?.renewal ?? '—';
  const rating = Number(b.rating || 0);
  const logo = b.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.name)}&background=076FF7&color=fff&bold=true&size=128`;
  const addr = [b.street_address, b.city, b.state_name].filter(Boolean).join(', ') || (b.city ?? '—');

  const queueCols: Column<any>[] = [
    { key: 'name', label: 'Queue', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.name}</Typography> },
    { key: 'category', label: 'Category' },
    { key: 'providers', label: 'Providers', align: 'right' },
    { key: 'util', label: 'Utilization', render: (r) => <ProgressBar booked={r.booked} capacity={r.capacity || 0} minWidth={140} /> },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
  ];
  const teamCols: Column<any>[] = [
    { key: 'name', label: 'Member', render: (r) => <NameCell name={r.name} /> },
    { key: 'role', label: 'Role', render: (r) => <StatusChip status={teamRoleStatus(r.role)} label={r.role} /> },
    { key: 'phone', label: 'Phone', render: (r) => r.phone || '—' },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
  ];

  return (
    <>
      <Crumbs items={[{ label: 'Businesses', to: '/businesses' }, { label: b.name }]} />
      <DetailHead
        title="Business Details (View)"
        actions={<StatusChip status={b.status} label={statusLabel(b.status)} />}
      />

      <Paper sx={{ p: 0, mb: 2.25, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.3fr 1fr' }, '& > div': { p: 2.75 }, '& > div + div': { borderLeft: { md: `1px solid ${colors.border}` }, borderTop: { xs: `1px solid ${colors.border}`, md: 'none' } } }}>
        <Box>
          <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
            <Box component="img" src={logo} alt="logo" sx={{ width: 64, height: 64, borderRadius: '16px', flexShrink: 0, objectFit: 'cover' }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 19, fontWeight: 800 }}>{b.name}</Typography>
                {b.is_verified && <VerifiedRounded sx={{ fontSize: 18, color: colors.primary }} />}
              </Box>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{b.category} · {b.city}</Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700, fontSize: 13, mt: 1 }}>
                <StarRounded sx={{ fontSize: 15, color: '#F59E0B' }} /> {rating.toFixed(1)}
                <Typography component="span" sx={{ color: colors.textTertiary, fontWeight: 500, fontSize: 12 }}>({b.total_ratings} ratings)</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box>
          <KV k="Business ID" v={<Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{b.id}</Box>} />
          <KV k="Owner" v={b.owner} />
          <KV k="Email" v={b.email} />
          <KV k="Phone" v={b.phone} />
          <KV k="Address" v={addr} />
          {b.website && <KV k="Website" v={<Box component="span" sx={{ color: colors.primary }}>{b.website}</Box>} />}
          {b.gstin && <KV k="GSTIN" v={b.gstin} />}
          <KV k="Joined On" v={b.joined} />
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Business Stats</Typography>
          <StatBox>
            <KV dashed k="Total Queues" v={s.total_queues} />
            <KV dashed k="Total Tokens" v={s.total_tokens} />
            <KV dashed k="Completed" v={s.completed_tokens} />
            <KV dashed k="Cancelled" v={s.cancelled_tokens} />
            <KV dashed k="Skipped" v={s.skipped_tokens} />
            <KV k="Lifetime Revenue" v={money(s.revenue)} />
          </StatBox>
        </Box>
      </Paper>

      <TabBar
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'queues', label: `Queues (${data.queues.length})` },
          { key: 'team', label: `Team (${data.team.length})` },
          { key: 'billing', label: 'Subscription & Billing' },
          { key: 'reviews', label: `Reviews (${data.reviews.count})` },
        ]}
      />

      {tab === 'overview' && (
        <Grid cols={2}>
          <SectionCard title="About Business">
            <Typography sx={{ color: 'text.secondary', fontSize: 13.5, lineHeight: 1.6 }}>
              {b.description || `${b.name} is a ${String(b.category).toLowerCase()} business based in ${b.city}. Owned by ${b.owner}, it uses BaseQueue smart token management to serve customers.`}
            </Typography>
          </SectionCard>
          <SectionCard title="Location">
            <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>{addr}</Typography>
            <Button component="a" href={'https://maps.google.com/?q=' + encodeURIComponent(addr)} target="_blank" size="small" sx={{ color: colors.primary, pl: 0, mt: 0.5 }}>
              View on Google Maps
            </Button>
          </SectionCard>
        </Grid>
      )}

      {tab === 'queues' && <Paper sx={{ p: 0 }}><DataTable columns={queueCols} rows={data.queues} emptyText="No queues." /></Paper>}
      {tab === 'team' && <Paper sx={{ p: 0 }}><DataTable columns={teamCols} rows={data.team} emptyText="No team members." /></Paper>}

      {tab === 'billing' && (
        <>
          <Grid cols={3}>
            <StatCard icon={<WorkspacePremiumRounded />} color="#076FF7" value={planLabel} label={sub ? `Status: ${statusLabel(String(sub.status))}` : 'Current Plan'} />
            <StatCard icon={<PaidRounded />} color="#16A34A" value={money(s.revenue)} label="Lifetime Revenue" />
            <StatCard icon={<EventRounded />} color="#7C3AED" value={renewal} label="Next Renewal" />
          </Grid>

          <SectionCard
            sx={{ mt: 2.25 }}
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardGiftcardRounded sx={{ fontSize: 19, color: colors.primary }} />
                Free Trial
                {trial && <StatusChip status={trial.is_active ? 'active' : 'expired'} label={trial.is_active ? 'Active' : statusLabel(trial.status)} />}
              </Box>
            }
            action={trial && (
              <Button size="small" variant="outlined" onClick={() => setEditTrial(true)} sx={{ borderColor: colors.border, color: colors.textPrimary }}>
                Edit trial
              </Button>
            )}
          >
            {trial ? (
              <>
                <StatBox>
                  <KV dashed k="Providers included" v={trial.provider_count} />
                  <KV dashed k="Tokens included" v={trial.token_count} />
                  <KV dashed k="Started" v={trial.start_date} />
                  <KV dashed k="Ends" v={trial.end_date} />
                  <KV k="Days remaining" v={trial.is_active ? trial.days_remaining : 0} />
                </StatBox>
                <Typography sx={{ fontSize: 12.5, color: colors.textTertiary, mt: 1.25 }}>
                  Changing the length re-counts from the start date. Platform defaults for new businesses live under
                  Trial Settings.
                </Typography>
              </>
            ) : (
              <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>
                This business never received a free trial.
              </Typography>
            )}
          </SectionCard>

          {trial && id && (
            <TrialEditDialog
              // Remount when the trial changes so the form reloads saved values.
              key={`${trial.end_date}-${trial.provider_count}-${trial.token_count}`}
              open={editTrial}
              businessId={id}
              trial={trial}
              onClose={() => setEditTrial(false)}
              onSaved={reload}
            />
          )}
        </>
      )}

      {tab === 'reviews' && (
        <Paper sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Ratings</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>avg {data.reviews.average} / 5 · {data.reviews.count} ratings</Typography>
          </Box>
          {data.reviews.items.length ? data.reviews.items.map((rv, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.75, borderBottom: `1px solid ${colors.border}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: 12, background: avatarGradient(rv.name) }}>{initials(rv.name)}</Box>
              <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13.5 }}>{rv.name}</Typography>
              <Stars n={rv.rating} />
              <Typography sx={{ color: 'text.secondary', fontSize: 12.5, ml: 1.75 }}>{rv.date}</Typography>
            </Box>
          )) : <Typography sx={{ color: 'text.secondary', fontSize: 13.5, p: 3, textAlign: 'center' }}>No ratings yet.</Typography>}
        </Paper>
      )}
    </>
  );
};

export default BusinessDetailPage;
